import debounce from 'lodash/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  isAbortError,
  TimeSeriesRequest,
  TimeSeriesResponse,
  TimeSeriesService,
  useTimeSeriesService,
} from './timeSeriesService'

interface CacheEntry {
  request: TimeSeriesRequest
  response: TimeSeriesResponse
  timestamp: number
}

interface UseTimeSeriesDataCacheOptions {
  debounceMs?: number
  onLoadingChange?: (isLoading: boolean) => void
  onError?: (error: Error) => void
  /**
   * Fetch pens at their real sample times where affordable, rather than
   * bucketing every pen. Constant for the life of the hook, so it is
   * deliberately absent from the cache key.
   */
  exactTimestamps?: boolean
}

interface PendingRequest {
  request: TimeSeriesRequest
  promise: Promise<TimeSeriesResponse>
}

export type TimeSeriesRequestStatus =
  | 'cached'
  | 'pending'
  | 'partial'
  | 'missing'

/**
 * Coarse-then-refine loading for large windows (see TWO_PHASE_LOAD in
 * config/constants.ts).
 *
 * When a fetch qualifies (large window, full resolution not already cached),
 * the load paints twice: `onProvisional` fires as soon as SOME renderable
 * data for the window exists — a slice of a cached superset window when one
 * covers the request, otherwise a cheap coarse-resolution fetch — and the
 * returned promise still resolves with the full-resolution result exactly as
 * before. Provisional data is a paint, not a truth: it is never written to
 * the cache under the request's key and never settles the load. The refine is
 * the load, so abort/supersede semantics are untouched — a newer request
 * cancels it exactly like any fetch, and `onProvisional` is suppressed the
 * moment the request stops being latest.
 */
export interface TwoPhaseLoadOptions {
  onProvisional: (response: TimeSeriesResponse) => void
}

/**
 * Custom hook that provides debounced and cached time series data fetching.
 * useSWR is not suitable here due to the need for fine-grained control (cancelling stale requests)
 * and frequent state changes (sensor selection, time range, etc).
 *
 * Features:
 * - Debounces fetch requests to avoid excessive API calls
 * - Caches data by sensor ID and time range
 * - Only fetches new data for changed parameters
 * - Handles loading states and errors
 */
const CACHE_MAX_AGE = 5 * 60 * 1000 // 5 minutes default

const getSortedEntryIds = (request: TimeSeriesRequest): string[] =>
  [...request.entry_ids].sort()

const buildTimeSeriesRequestKey = (request: TimeSeriesRequest): string =>
  JSON.stringify({
    entry_ids: getSortedEntryIds(request),
    start_datetime: request.start_datetime,
    end_datetime: request.end_datetime,
    interval: request.interval,
    interval_unit: request.interval_unit,
    aggregation_op: request.aggregation_op,
    interpolate: request.interpolate,
  })

const requestParametersMatch = (
  req1: TimeSeriesRequest,
  req2: TimeSeriesRequest
): boolean => {
  return (
    req1.start_datetime === req2.start_datetime &&
    req1.end_datetime === req2.end_datetime &&
    req1.interval === req2.interval &&
    req1.interval_unit === req2.interval_unit &&
    req1.aggregation_op === req2.aggregation_op &&
    req1.interpolate === req2.interpolate
  )
}

export const useTimeSeriesDataCache = (
  options: UseTimeSeriesDataCacheOptions = {}
) => {
  const {
    debounceMs = 500,
    onLoadingChange,
    onError,
    exactTimestamps = false,
  } = options

  const { fetchTimeSeriesData } = useTimeSeriesService()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Cache structure: Map<sensorId, CacheEntry[]>
  const cacheRef = useRef<Map<string, CacheEntry[]>>(new Map())

  // Track pending requests to avoid duplicate fetches
  const pendingRequestsRef = useRef<Map<string, PendingRequest>>(new Map())

  // Track the latest request parameters
  const latestRequestRef = useRef<TimeSeriesRequest | null>(null)

  // Track the current AbortController to cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Registry of loads currently in flight -- one token per `fetchData` call
   * that actually went (or is going) to the network.
   *
   * `isLoading` is DERIVED from this registry: it is true exactly while the
   * LATEST request still has an unsettled load. It used to be paired
   * imperative toggles spread across three racing paths (immediate, debounced
   * schedule, debounced run) sharing one flag and one abort controller, and
   * the pairs were guarded inconsistently: the debounced run's `finally`
   * checked `latestRequestRef`, but the immediate path's did not. So when a
   * newer debounced run aborted the shared controller, the superseded
   * immediate call's completion unconditionally rewrote the flag (and nulled
   * the shared controller ref) while the newer request was still mid-flight
   * -- the loading indicator tracked the wrong request's lifecycle. Deriving
   * the flag makes every completion idempotent: a superseded load's settle
   * just removes its own token, and the flag recomputes from whoever is
   * actually the latest.
   */
  const activeLoadsRef = useRef<Set<{ request: TimeSeriesRequest }>>(new Set())

  /**
   * Loads owned by the debounced path, keyed by request identity, so the
   * debounced run (and a superseding `fetchData`) can settle the load token
   * created when the fetch was scheduled.
   */
  const debouncedLoadsRef = useRef<
    Map<TimeSeriesRequest, { request: TimeSeriesRequest }>
  >(new Map())

  // Read through a ref so the loading-state helpers stay referentially stable
  // (the debounced run is built once and closes over them).
  const onLoadingChangeRef = useRef(onLoadingChange)
  onLoadingChangeRef.current = onLoadingChange

  const lastReportedLoadingRef = useRef(false)

  /**
   * Recompute `isLoading` from the registry: loading while the latest request
   * still has an unsettled load. Superseded loads are ignored -- their data
   * will not render, so they must not drive the indicator.
   */
  const syncLoadingState = useCallback(() => {
    const latest = latestRequestRef.current
    let stillLoading = false
    for (const load of activeLoadsRef.current) {
      if (load.request === latest) {
        stillLoading = true
        break
      }
    }

    setIsLoading(stillLoading)
    if (lastReportedLoadingRef.current !== stillLoading) {
      lastReportedLoadingRef.current = stillLoading
      onLoadingChangeRef.current?.(stillLoading)
    }
  }, [])

  const beginLoad = useCallback(
    (request: TimeSeriesRequest): { request: TimeSeriesRequest } => {
      const load = { request }
      activeLoadsRef.current.add(load)
      syncLoadingState()
      return load
    },
    [syncLoadingState]
  )

  /** Idempotent: settling a load that was already settled is a no-op. */
  const endLoad = useCallback(
    (load: { request: TimeSeriesRequest } | undefined) => {
      if (!load) return
      activeLoadsRef.current.delete(load)
      syncLoadingState()
    },
    [syncLoadingState]
  )

  /** Settle the debounced-path load for `request`, wherever it got to. */
  const endDebouncedLoad = useCallback(
    (request: TimeSeriesRequest) => {
      const load = debouncedLoadsRef.current.get(request)
      debouncedLoadsRef.current.delete(request)
      endLoad(load)
    },
    [endLoad]
  )

  /**
   * Find cached data for a specific sensor and time range
   */
  const findCachedData = useCallback(
    (
      sensorId: string,
      request: TimeSeriesRequest
    ): TimeSeriesResponse[string] | null => {
      const sensorCache = cacheRef.current.get(sensorId)
      if (!sensorCache) return null

      const now = Date.now()
      const validEntry = sensorCache.find(
        (entry) =>
          requestParametersMatch(entry.request, request) &&
          now - entry.timestamp < CACHE_MAX_AGE
      )

      return validEntry ? validEntry.response[sensorId] : null
    },
    []
  )

  /**
   * Rows for `request`'s window sliced out of a cached entry whose window
   * CONTAINS it (same aggregation/interpolate; interval deliberately ignored —
   * the superset's coarser-or-equal buckets are fine for an immediate paint
   * that a refine will replace). Zoom-out (request ⊃ cached) never matches:
   * partial coverage is not stitched. Among multiple containing entries the
   * narrowest window wins, since its buckets are the finest.
   *
   * Read-only by design: no cache write, no pending-request registration, so
   * it can never dedup-swallow or cancel the refine fetch that follows it.
   */
  const findSupersetSlice = useCallback(
    (
      sensorId: string,
      request: TimeSeriesRequest
    ): TimeSeriesResponse[string] | null => {
      const sensorCache = cacheRef.current.get(sensorId)
      if (!sensorCache) return null

      const requestStartMs = Date.parse(request.start_datetime)
      const requestEndMs = Date.parse(request.end_datetime)
      if (Number.isNaN(requestStartMs) || Number.isNaN(requestEndMs))
        return null

      const now = Date.now()
      let best: { entry: CacheEntry; spanMs: number } | null = null
      for (const entry of sensorCache) {
        if (now - entry.timestamp >= CACHE_MAX_AGE) continue
        if (
          entry.request.aggregation_op !== request.aggregation_op ||
          entry.request.interpolate !== request.interpolate
        )
          continue

        const entryStartMs = Date.parse(entry.request.start_datetime)
        const entryEndMs = Date.parse(entry.request.end_datetime)
        if (entryStartMs > requestStartMs || entryEndMs < requestEndMs) continue

        const spanMs = entryEndMs - entryStartMs
        if (!best || spanMs < best.spanMs) {
          best = { entry, spanMs }
        }
      }
      if (!best) return null

      const rows = best.entry.response[sensorId]
      if (!rows) return null

      return rows.filter((point) => {
        const pointMs = Date.parse(point.timestamp)
        return pointMs >= requestStartMs && pointMs <= requestEndMs
      })
    },
    []
  )

  /**
   * Add data to cache
   */
  const addToCache = useCallback(
    (
      sensorId: string,
      request: TimeSeriesRequest,
      data: TimeSeriesResponse[string]
    ) => {
      const sensorCache = cacheRef.current.get(sensorId) || []

      // Remove old entries for the same time range
      const filteredCache = sensorCache.filter(
        (entry) => !requestParametersMatch(entry.request, request)
      )

      // Add new entry
      filteredCache.push({
        request,
        response: { [sensorId]: data },
        timestamp: Date.now(),
      })

      // Keep only the most recent entries (limit to 10 per sensor)
      if (filteredCache.length > 10) {
        filteredCache.sort((a, b) => b.timestamp - a.timestamp)
        filteredCache.splice(10)
      }

      cacheRef.current.set(sensorId, filteredCache)
    },
    []
  )

  const findPendingRequestForSensor = useCallback(
    (sensorId: string, request: TimeSeriesRequest): PendingRequest | null => {
      for (const pendingRequest of pendingRequestsRef.current.values()) {
        if (
          pendingRequest.request.entry_ids.includes(sensorId) &&
          requestParametersMatch(pendingRequest.request, request)
        ) {
          return pendingRequest
        }
      }

      return null
    },
    []
  )

  /**
   * Clear expired cache entries
   */
  const clearExpiredCache = useCallback(() => {
    const now = Date.now()
    cacheRef.current.forEach((entries, sensorId) => {
      const validEntries = entries.filter(
        (entry) => now - entry.timestamp < CACHE_MAX_AGE
      )
      if (validEntries.length > 0) {
        cacheRef.current.set(sensorId, validEntries)
      } else {
        cacheRef.current.delete(sensorId)
      }
    })
  }, [])

  const getRequestStatus = useCallback(
    (request: TimeSeriesRequest): TimeSeriesRequestStatus => {
      clearExpiredCache()

      if (request.entry_ids.length === 0) {
        return 'cached'
      }

      let cachedCount = 0
      let pendingCount = 0
      let missingCount = 0

      for (const sensorId of request.entry_ids) {
        const cachedData = findCachedData(sensorId, request)
        if (cachedData) {
          cachedCount += 1
          continue
        }

        const pendingRequest = findPendingRequestForSensor(sensorId, request)
        if (pendingRequest) {
          pendingCount += 1
          continue
        }

        missingCount += 1
      }

      if (cachedCount === request.entry_ids.length) {
        return 'cached'
      }

      if (missingCount === 0 && pendingCount > 0) {
        return 'pending'
      }

      if (cachedCount > 0 || pendingCount > 0) {
        return 'partial'
      }

      return 'missing'
    },
    [clearExpiredCache, findCachedData, findPendingRequestForSensor]
  )

  const getPendingPromisesForRequest = useCallback(
    (request: TimeSeriesRequest): Promise<TimeSeriesResponse>[] => {
      const pendingPromises = new Set<Promise<TimeSeriesResponse>>()

      for (const sensorId of request.entry_ids) {
        const pendingRequest = findPendingRequestForSensor(sensorId, request)
        if (pendingRequest) {
          pendingPromises.add(pendingRequest.promise)
        }
      }

      return [...pendingPromises]
    },
    [findPendingRequestForSensor]
  )

  /**
   * Self-reference so the two-phase coarse pass can recurse through the same
   * cache/dedup machinery as any other fetch. Assigned immediately after the
   * callback is created, so it is always set by the time a fetch runs.
   */
  const fetchWithCacheRef = useRef<
    | ((
        request: TimeSeriesRequest,
        signal?: AbortSignal,
        perfSeq?: number,
        twoPhase?: TwoPhaseLoadOptions
      ) => Promise<TimeSeriesResponse>)
    | null
  >(null)

  /**
   * Fetch data with caching logic
   */
  const fetchWithCache = useCallback(
    async (
      request: TimeSeriesRequest,
      signal?: AbortSignal,
      perfSeq?: number,
      twoPhase?: TwoPhaseLoadOptions
    ): Promise<TimeSeriesResponse> => {
      // Clear expired cache entries
      clearExpiredCache()

      const result: TimeSeriesResponse = {}
      const sensorsToFetch: string[] = []

      // Check cache for each sensor
      for (const sensorId of request.entry_ids) {
        const cachedData = findCachedData(sensorId, request)
        if (cachedData) {
          result[sensorId] = cachedData
        } else {
          sensorsToFetch.push(sensorId)
        }
      }

      // If all data is cached, return immediately
      if (sensorsToFetch.length === 0) {
        return result
      }

      // Create a unique key for this request
      const fetchRequest = {
        ...request,
        entry_ids: sensorsToFetch,
      }
      const requestKey = buildTimeSeriesRequestKey(fetchRequest)

      // Piggyback on an identical in-flight request when there is one.
      const pendingRequest = pendingRequestsRef.current.get(requestKey)
      if (pendingRequest) {
        try {
          const pendingData = await pendingRequest.promise
          return { ...result, ...pendingData }
        } catch (error) {
          // The request we attached to was aborted or failed by whoever owned
          // it — aborts fire on a shared controller whenever a newer fetch
          // starts, so this says nothing about whether WE still need the data.
          // Inheriting that rejection stranded the chart empty with no retry
          // until the time range changed. Fall through and issue our own.
          if (signal?.aborted) throw error
          if (pendingRequestsRef.current.get(requestKey) === pendingRequest) {
            pendingRequestsRef.current.delete(requestKey)
          }
        }
      }

      // Two-phase (coarse-then-refine) first paint for large windows. Reached
      // only when a real full-resolution fetch is unavoidable: full-res cache
      // hits returned above, and an identical in-flight fetch was piggybacked
      // above — so a repeat visit to a cached window never re-runs phase 1.
      if (twoPhase) {
        const coarseRequest =
          TimeSeriesService.buildCoarsePhaseRequest(fetchRequest)
        if (coarseRequest) {
          const emitProvisional = (provisional: TimeSeriesResponse) => {
            // A superseded request's paint would clobber the newer window's
            // chart, so provisional data only flows while this request is
            // still the latest. `request` is the exact object `fetchData`
            // installed as latest, so identity comparison is sound.
            if (latestRequestRef.current !== request || signal?.aborted) return
            twoPhase.onProvisional({ ...result, ...provisional })
          }

          // Instant paint when a cached window CONTAINS this one (zoom-in):
          // slice the superset's rows to the requested window. All-or-nothing
          // across sensors — partial coverage is not stitched.
          const sliced: TimeSeriesResponse = {}
          let sliceCoversAllSensors = true
          let sliceHasRows = false
          for (const sensorId of sensorsToFetch) {
            const slice = findSupersetSlice(sensorId, fetchRequest)
            if (slice === null) {
              sliceCoversAllSensors = false
              break
            }
            sliced[sensorId] = slice
            sliceHasRows = sliceHasRows || slice.length > 0
          }

          if (sliceCoversAllSensors && sliceHasRows) {
            emitProvisional(sliced)
          } else {
            // Coarse network pass. Recursing through fetchWithCache gives it
            // the same cache/dedup treatment as any fetch: a repeat load
            // within TTL serves the coarse pass straight from cache, and the
            // coarse entry lives under its own interval key so it can never
            // shadow the full-resolution entry.
            try {
              const coarseData = await fetchWithCacheRef.current!(
                coarseRequest,
                signal
              )
              emitProvisional(coarseData)
            } catch (coarseError) {
              // An aborted coarse pass means the whole load was superseded.
              if (isAbortError(coarseError)) throw coarseError
              // Any other coarse failure is ignorable: phase 1 is an
              // optimisation, and the full fetch below is the real load.
            }
          }
        }
      }

      // Fetch only the missing data
      const fetchPromise = fetchTimeSeriesData(fetchRequest, {
        signal,
        perfSeq,
        exactTimestamps,
      })

      // Store the pending request
      pendingRequestsRef.current.set(requestKey, {
        request: fetchRequest,
        promise: fetchPromise,
      })

      try {
        const newData = await fetchPromise

        // Add to cache
        Object.entries(newData).forEach(([sensorId, data]) => {
          addToCache(sensorId, request, data)
        })

        // Combine cached and new data
        return { ...result, ...newData }
      } finally {
        // Remove from pending requests
        pendingRequestsRef.current.delete(requestKey)
      }
    },
    [
      clearExpiredCache,
      fetchTimeSeriesData,
      findCachedData,
      findSupersetSlice,
      addToCache,
      exactTimestamps,
    ]
  )
  fetchWithCacheRef.current = fetchWithCache

  const prefetchData = useCallback(
    async (request: TimeSeriesRequest): Promise<void> => {
      if (request.entry_ids.length === 0) {
        return
      }

      const requestStatus = getRequestStatus(request)
      if (requestStatus === 'cached') {
        return
      }

      if (requestStatus === 'pending') {
        await Promise.allSettled(getPendingPromisesForRequest(request))
        return
      }

      try {
        await fetchWithCache(request)
      } catch {
        // Prefetch is opportunistic and must not surface errors in the UI.
      }
    },
    [fetchWithCache, getPendingPromisesForRequest, getRequestStatus]
  )

  /**
   * Resolve/reject callbacks for the caller currently awaiting a debounced
   * fetch, tagged with the request they belong to.
   *
   * The tag is load-bearing. There is only ever one slot, but a debounced run is
   * async: between the moment it starts and the moment its await returns, a
   * newer `fetchData` can replace the occupant. An untagged slot let a run
   * settle whoever happened to be sitting in it, which crossed the wires in both
   * directions -- see `settlePendingCallbacks`.
   */
  const pendingCallbacksRef = useRef<{
    request: TimeSeriesRequest
    resolve: (value: TimeSeriesResponse) => void
    reject: (error: Error) => void
  } | null>(null)

  /**
   * Settle the awaiting caller, but only if it is the one that asked for
   * `request`.
   *
   * Without the ownership check, an in-flight run for a superseded window A
   * would settle the caller waiting on window B:
   *   - A resolves first  -> B's caller receives A's points, and the chart draws
   *     the previous window's data while `timeRange` reads the new one. uPlot
   *     auto-ranges x to the data it is handed, so the axis follows the stale
   *     window too (bug: "chopped chart / axis does not match the range").
   *   - A is aborted first -> B's caller inherits A's AbortError, which the
   *     consumer treats as cancellation and silently drops (bug: "no data until
   *     I pan or zoom again").
   * In both cases B's own response then arrived to find the slot already empty
   * and was discarded, so nothing ever repaired the chart.
   *
   * Returns whether it settled, so callers can tell "not mine" from "done".
   */
  const settlePendingCallbacks = useCallback(
    (
      request: TimeSeriesRequest,
      settle: (
        callbacks: NonNullable<typeof pendingCallbacksRef.current>
      ) => void
    ): boolean => {
      const pending = pendingCallbacksRef.current
      if (!pending || pending.request !== request) return false

      pendingCallbacksRef.current = null
      settle(pending)
      return true
    },
    []
  )

  // `debouncedFetch` is built once, so it must reach the latest
  // `settlePendingCallbacks` through a ref rather than closing over it.
  const settlePendingCallbacksRef = useRef(settlePendingCallbacks)
  settlePendingCallbacksRef.current = settlePendingCallbacks

  /**
   * The main fetch function (debounced)
   */
  const debouncedFetch = useRef(
    debounce(
      async (
        request: TimeSeriesRequest,
        perfSeq?: number,
        twoPhase?: TwoPhaseLoadOptions
      ) => {
        // Only proceed if this is still the latest request. `fetchData` cancels
        // the timer before scheduling a newer one, so this is belt-and-braces --
        // but if it ever does hit, the caller must not be left hanging forever.
        if (latestRequestRef.current !== request) {
          settlePendingCallbacksRef.current(request, ({ reject }) =>
            reject(new Error('Request cancelled'))
          )
          endDebouncedLoad(request)
          return
        }

        // Cancel any existing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Create new AbortController for this request
        const controller = new AbortController()
        abortControllerRef.current = controller
        const { signal } = controller

        setError(null)

        try {
          const result = await fetchWithCache(
            request,
            signal,
            perfSeq,
            twoPhase
          )

          // Resolve the pending promise -- but only if it is still OUR caller
          // waiting. A newer request may have taken the slot while we awaited.
          settlePendingCallbacksRef.current(request, ({ resolve }) =>
            resolve(result)
          )

          return result
        } catch (err) {
          // Check if this was an abort - if so, don't treat it as an error
          if (isAbortError(err)) {
            // Don't set error state or call onError for aborted requests.
            // Aborts fire on a shared controller whenever a newer fetch starts, so
            // this abort usually means "someone else superseded us" -- which is
            // precisely when the slot belongs to that someone else, and rejecting
            // it would strand their chart empty.
            settlePendingCallbacksRef.current(request, ({ reject }) =>
              reject(
                err instanceof Error ? err : new Error('Request cancelled')
              )
            )
            return {} // Return empty response for aborted requests
          }

          const error = err instanceof Error ? err : new Error('Unknown error')

          // A failure for a superseded window must not surface as the current
          // window's error state -- the newer request is still on its way.
          if (latestRequestRef.current === request) {
            setError(error)
            onError?.(error)
          }

          // Reject the pending promise
          settlePendingCallbacksRef.current(request, ({ reject }) =>
            reject(error)
          )

          throw error
        } finally {
          // Settle this run's load token. The registry derives the flag, so a
          // superseded run settling here can never switch loading off (or leave
          // it on) under the request that superseded it.
          endDebouncedLoad(request)

          // Clear the shared controller ref only if it is still OURS -- a newer
          // fetch may have installed its own controller, which must stay
          // reachable so the next fetch can abort it.
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null
          }
        }
      },
      debounceMs
    )
  ).current

  /**
   * Clear the cache (useful for manual refresh)
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear()
    pendingRequestsRef.current.clear()
  }, [])

  const evictMatchingCacheEntries = useCallback(
    (request: TimeSeriesRequest) => {
      for (const sensorId of request.entry_ids) {
        const sensorCache = cacheRef.current.get(sensorId)
        if (!sensorCache) continue

        const remainingEntries = sensorCache.filter(
          (entry) => !requestParametersMatch(entry.request, request)
        )

        if (remainingEntries.length > 0) {
          cacheRef.current.set(sensorId, remainingEntries)
        } else {
          cacheRef.current.delete(sensorId)
        }
      }
    },
    []
  )

  /**
   * Public fetch function that consumers will call
   * @param request - The time series request parameters
   * @param options - Optional parameters
   * @param options.bypassDebounce - If true, fetches immediately without debouncing
   */
  const fetchData = useCallback(
    async (
      request: TimeSeriesRequest,
      options?: {
        bypassDebounce?: boolean
        /** Skip the debounce delay but still serve from cache when possible. */
        immediate?: boolean
        perfSeq?: number
        /**
         * Opt in to the coarse-then-refine first paint for large windows.
         * Ignored whenever the full-resolution window is already cached.
         */
        twoPhase?: TwoPhaseLoadOptions
      }
    ): Promise<TimeSeriesResponse> => {
      // Store the latest request
      latestRequestRef.current = request

      // Cancel any pending debounced calls and callbacks
      debouncedFetch.cancel()

      // If there's a pending callback, reject it -- and settle its load, since
      // cancelling the timer means no run will ever settle it for us.
      const cancelledCaller = pendingCallbacksRef.current
      if (cancelledCaller) {
        pendingCallbacksRef.current = null
        cancelledCaller.reject(new Error('Request cancelled'))
        endDebouncedLoad(cancelledCaller.request)
      }

      // If no sensors selected, return empty immediately
      if (request.entry_ids.length === 0) {
        syncLoadingState()
        return {}
      }

      // Check if all data is cached
      const immediateResult: TimeSeriesResponse = {}
      let hasAllCached = true

      for (const sensorId of request.entry_ids) {
        const cachedData = findCachedData(sensorId, request)
        if (cachedData) {
          immediateResult[sensorId] = cachedData
        } else {
          hasAllCached = false
        }
      }

      // If all data is cached, return it immediately
      if (
        !options?.bypassDebounce &&
        hasAllCached &&
        request.entry_ids.length > 0
      ) {
        syncLoadingState()
        return immediateResult
      }

      // Register this call's load; `isLoading` derives from the registry, so
      // it reads true from here until THIS request's load settles (or a newer
      // request takes over as latest).
      const load = beginLoad(request)

      // `bypassDebounce` forces a refresh (evicting the cached window);
      // `immediate` only skips the debounce delay, keeping cache semantics
      // intact. The debounce exists to coalesce slider drags, so making the
      // first load of a page wait for it is pure latency.
      if (options?.bypassDebounce || options?.immediate) {
        if (options.bypassDebounce) {
          // Refresh only this request while keeping adjacent prefetched windows.
          evictMatchingCacheEntries(request)
        }
        // Cancel any existing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Create new AbortController for this request
        const controller = new AbortController()
        abortControllerRef.current = controller
        const { signal } = controller

        try {
          return await fetchWithCache(
            request,
            signal,
            options.perfSeq,
            options.twoPhase
          )
        } catch (err) {
          // An abort is NOT an empty result. Returning `{}` here told the caller
          // "this window has no data", which it would faithfully render as a
          // blank chart with no error and nothing to retry. Propagate instead,
          // so the caller can tell "cancelled, keep what you have" apart from
          // "the server really returned nothing".
          if (isAbortError(err)) {
            throw err
          }

          const error = err instanceof Error ? err : new Error('Unknown error')
          setError(error)
          onError?.(error)
          throw error
        } finally {
          // Settle only OUR load. When this call has been superseded (a newer
          // debounced run aborted our controller), the newer request's load is
          // still in the registry, so the derived flag stays true for it --
          // this path used to clear the flag unconditionally and switch the
          // indicator off under the still-loading successor.
          endLoad(load)

          // Likewise, only clear the shared controller ref if it is still
          // ours; nulling a successor's controller made its fetch unabortable.
          if (abortControllerRef.current === controller) {
            abortControllerRef.current = null
          }
        }
      }

      // Otherwise, use debounced fetch. The load token travels with the
      // request so the debounced run (or a superseding `fetchData`) can settle
      // it.
      debouncedLoadsRef.current.set(request, load)
      return new Promise<TimeSeriesResponse>((resolve, reject) => {
        pendingCallbacksRef.current = { request, resolve, reject }

        // Call the debounced function
        debouncedFetch(request, options?.perfSeq, options?.twoPhase)
      })
    },
    [
      debouncedFetch,
      findCachedData,
      fetchWithCache,
      onError,
      evictMatchingCacheEntries,
      beginLoad,
      endLoad,
      endDebouncedLoad,
      syncLoadingState,
    ]
  )

  /**
   * Get current cache statistics
   */
  const getCacheStats = useCallback(() => {
    let totalEntries = 0
    const totalSensors = cacheRef.current.size

    cacheRef.current.forEach((entries) => {
      totalEntries += entries.length
    })

    return {
      totalSensors,
      totalEntries,
      cacheSize: cacheRef.current.size,
    }
  }, [])

  // Cleanup function to abort any in-flight requests
  useEffect(() => {
    return () => {
      // Cancel any pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      debouncedFetch.cancel()
    }
  }, [debouncedFetch])

  return {
    fetchData,
    prefetchData,
    getRequestStatus,
    isLoading,
    error,
    clearCache,
    getCacheStats,
  }
}
